import React from "react";

import TextField from "@mui/material/TextField";
import { Row, Col } from "react-bootstrap";

const padTo2Digits = (num) => num.toString().padStart(2, "0");

const convertTimeString = (value) => {
  if (!value) {
    return "";
  }
  // If value is a string in HH:mm or HH:mm:ss format, return as is (first 5 chars)
  if (typeof value === "string") {
    const directMatch = value.match(/^(\d{2}):(\d{2})/);
    if (directMatch) {
      return `${directMatch[1]}:${directMatch[2]}`;
    }
    // If value is ISO string, extract time part as local
    const isoMatch = value.match(/T(\d{2}):(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}:${isoMatch[2]}`;
    }
  }
  // Fallback: do not use Date object to avoid timezone shift, just return empty
  return "";
};

const formatDateForInput = (date) => {
  if (!date) {
    return "";
  }
  if (typeof date === "string") {
    const match = date.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      return match[1];
    }
  }
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return `${parsedDate.getFullYear()}-${padTo2Digits(
    parsedDate.getMonth() + 1
  )}-${padTo2Digits(parsedDate.getDate())}`;
};

const getTimeFieldValue = (clinicData, field) => {
  if (!clinicData || !clinicData[field]) {
    return "";
  }
  return convertTimeString(clinicData[field]);
};

const xs = { span: 6 };

const getIsoForTime = (dateSource, hours, minutes) => {
  const hourStr = padTo2Digits(hours);
  const minuteStr = padTo2Digits(minutes);
  let baseDate = "1970-01-01";

  if (typeof dateSource === "string") {
    if (dateSource.includes("T")) {
      baseDate = dateSource.split("T")[0];
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateSource)) {
      baseDate = dateSource;
    }
  }

  return `${baseDate}T${hourStr}:${minuteStr}:00`;
};

const handleTimeChange = (timeValue, field, props, baseDateOverride) => {
  if (!timeValue) {
    props.SetClinicData((ClinicData) => ({
      ...ClinicData,
      ...{ [field]: null },
    }));
    return;
  }
  const [hours, minutes] = timeValue.split(":").map((part) => Number(part));
  const baseDateSource =
    baseDateOverride || (props.ClinicData ? props.ClinicData[field] : null);
  const updatedTimeIso = getIsoForTime(baseDateSource, hours, minutes);
  props.SetClinicData((ClinicData) => ({
    ...ClinicData,
    ...{ [field]: updatedTimeIso },
  }));
};

const handleDateChange = (e, field, props) => {
  const { value } = e.target;
  if (!value) {
    props.SetClinicData((ClinicData) => ({
      ...ClinicData,
      ...{ [field]: null },
    }));
    return;
  }
  props.SetClinicData((ClinicData) => ({
    ...ClinicData,
    ...{ [field]: `${value}T00:00:00` },
  }));
};

function HolidayOpenCloseTimes(props) {
  const prefix = props.DepartmentPrefix || "";
  const suffix = props.HolidaySuffix || "";
  const dateField = `${prefix}HolidayStartDate${suffix}`;

  const fieldNames = React.useMemo(
    () => ({
      overnightStart: `${prefix}HolidayOvernightStartTime${suffix}`,
      overnightEnd: `${prefix}HolidayOvernightEndTime${suffix}`,
      dayStart: `${prefix}HolidayStartTime${suffix}`,
      dayEnd: `${prefix}HolidayEndTime${suffix}`,
    }),
    [prefix, suffix]
  );

  const clinicHolidayDate =
    props.ClinicData &&
    props.ClinicData[dateField];
  const [holidayDate, setHolidayDate] = React.useState(
    formatDateForInput(clinicHolidayDate)
  );

  const [timeValues, setTimeValues] = React.useState(() => ({
    overnightStart: getTimeFieldValue(
      props.ClinicData,
      fieldNames.overnightStart
    ),
    overnightEnd: getTimeFieldValue(
      props.ClinicData,
      fieldNames.overnightEnd
    ),
    dayStart: getTimeFieldValue(props.ClinicData, fieldNames.dayStart),
    dayEnd: getTimeFieldValue(props.ClinicData, fieldNames.dayEnd),
  }));

  React.useEffect(() => {
    setHolidayDate((current) => {
      const formatted = formatDateForInput(clinicHolidayDate);
      return current === formatted ? current : formatted;
    });
  }, [clinicHolidayDate]);

  React.useEffect(() => {
    setTimeValues({
      overnightStart: getTimeFieldValue(
        props.ClinicData,
        fieldNames.overnightStart
      ),
      overnightEnd: getTimeFieldValue(
        props.ClinicData,
        fieldNames.overnightEnd
      ),
      dayStart: getTimeFieldValue(props.ClinicData, fieldNames.dayStart),
      dayEnd: getTimeFieldValue(props.ClinicData, fieldNames.dayEnd),
    });
  }, [
    props.ClinicData,
    fieldNames.overnightStart,
    fieldNames.overnightEnd,
    fieldNames.dayStart,
    fieldNames.dayEnd,
  ]);

  return (
    props.ClinicData && (
      <div key={props.ClinicData.DNIS}>
        <Row>
          <Col style={{ fontWeight: "600" }} md={2} xs={xs}>
            Overnight Opening Time
          </Col>
          <Col style={{ fontWeight: "600" }} md={2} xs={xs}>
            Overnight Closing Time
          </Col>
          <Col style={{ fontWeight: "600" }} md={2} xs={xs}>
            Opening Time
          </Col>
          <Col style={{ fontWeight: "600" }} md={2} xs={xs}>
            Closing Time
          </Col>

          <Col style={{ fontWeight: "600" }} md={4} xs={xs}>
            Date
          </Col>
        </Row>
        <Row>
          <Col md={2} xs={xs}>
            <TextField
              id={
                fieldNames.overnightStart
              }
              variant="outlined"
              type="time"
              onChange={(e) => {
                const { value } = e.target;
                setTimeValues((current) => ({
                  ...current,
                  overnightStart: value,
                }));
                handleTimeChange(
                  value,
                  fieldNames.overnightStart,
                  props,
                  holidayDate
                );
              }}
              value={timeValues.overnightStart}
            />
          </Col>
          <Col md={2} xs={xs}>
            <TextField
              id={
                fieldNames.overnightEnd
              }
              variant="outlined"
              type="time"
              onChange={(e) => {
                const { value } = e.target;
                setTimeValues((current) => ({
                  ...current,
                  overnightEnd: value,
                }));
                handleTimeChange(
                  value,
                  fieldNames.overnightEnd,
                  props,
                  holidayDate
                );
              }}
              value={timeValues.overnightEnd}
            />
          </Col>
          <Col md={2} xs={xs}>
            <TextField
              id={
                fieldNames.dayStart
              }
              variant="outlined"
              type="time"
              onChange={(e) => {
                const { value } = e.target;
                setTimeValues((current) => ({
                  ...current,
                  dayStart: value,
                }));
                handleTimeChange(
                  value,
                  fieldNames.dayStart,
                  props,
                  holidayDate
                );
              }}
              value={timeValues.dayStart}
            />
          </Col>
          <Col md={2} xs={xs}>
            <TextField
              id={
                fieldNames.dayEnd
              }
              variant="outlined"
              type="time"
              onChange={(e) => {
                const { value } = e.target;
                setTimeValues((current) => ({
                  ...current,
                  dayEnd: value,
                }));
                handleTimeChange(
                  value,
                  fieldNames.dayEnd,
                  props,
                  holidayDate
                );
              }}
              value={timeValues.dayEnd}
            />
          </Col>

          <Col md={4} xs={xs}>
            <TextField
              id={
                dateField
              }
              variant="outlined"
              type="date"
              onChange={(e) =>
                {
                  setHolidayDate(e.target.value);
                  handleDateChange(
                    e,
                    dateField,
                    props
                  );
                }
              }
              value={holidayDate}
            />
          </Col>
        </Row>
      </div>
    )
  );
}

export default HolidayOpenCloseTimes;
